/**
 * Pre-login tenant resolution for the PWA (mirrors the shell's
 * tenant-resolver): the tenant is resolved by HOST from tenant-service's
 * public branding projection, never from a client-supplied id. The resolved
 * tenantId is what `api-client` sends as `X-Tenant-ID` (via the auth store's
 * `tenantConfig`) and what the auth screens pass to `sessionClient` — the
 * backend's `TenantResolutionFilter` fails closed without it
 * (MISSING_TENANT_400_001), including on the public /auth/* routes.
 */
import { fetchPublicBranding } from '@quickbee/api-tenant';
import { useAuthStore, type TenantConfig } from '@quickbee/auth';
import type { SessionClientOptions } from '@quickbee/auth/internal';
import { getDevSession, isAuthDisabled } from '@quickbee/api-config';

/** Emitted at most once per page load — a warning per render would be noise. */
let warnedFallback = false;

export async function initTenant(): Promise<void> {
  // Local no-auth development: the synthetic dev session already carries the
  // tenant id, and api-client's devIdentity uses it on every call.
  if (isAuthDisabled() && getDevSession()) return;

  const branding = await fetchPublicBranding(window.location.hostname);
  if (!branding) {
    if (!warnedFallback) {
      warnedFallback = true;
      console.warn(
        `[tenant] Public branding for host "${window.location.hostname}" could not be ` +
          'loaded — no tenant id is available, so authenticated calls will be refused ' +
          'by TenantResolutionFilter. If this is a deployed environment, ' +
          'GET /tenants/public/by-host/{host} is missing or unrouted ' +
          '(backend-api-integration-design.md §16 B6).'
      );
    }
    return;
  }

  const config: TenantConfig = {
    tenantId: branding.tenantId,
    name: branding.name,
    logoUrl: branding.logoUrl ?? '',
    primaryColor: branding.primaryColor,
    secondaryColor: branding.secondaryColor,
    fontFamily: branding.fontFamily ?? 'default',
    appName: branding.appName,
    themeMode: branding.themeMode ?? 'system',
    // Not part of the public projection — populated post-login if ever needed.
    schemes: [],
    featureFlags: branding.publicFeatureFlags ?? {},
    allowedRoles: ['SAVER'],
    supportEmail: branding.supportEmail ?? '',
    tnc_url: branding.tncUrl ?? ''
  };
  useAuthStore.getState().setTenantConfig(config);
}

/**
 * Options for every `@quickbee/auth/internal` sessionClient call: carries the
 * resolved tenant id so `X-Tenant-ID` is sent on register / login / verify /
 * refresh / logout. Returns `{}` only when no tenant could be resolved (the
 * server will then answer MISSING_TENANT_400_001, surfaced inline).
 */
export function sessionOptions(): SessionClientOptions {
  const state = useAuthStore.getState();
  const tenantId = state.user?.tenantId ?? state.tenantConfig?.tenantId;
  return tenantId ? { tenantId } : {};
}
